import { ref, computed, watch } from 'vue';
import {
  THEME_KEY,
  DEFAULT_SCHEME,
  WEB_PRESETS,
  UNI_PRESETS,
  WEB_LAYOUT,
  UNI_LAYOUT,
  SCHEME_META,
  REMOTE_THEME_KEY,
} from '../constants/theme.js';
import { cache } from '../utils/cache.js';

const isUniApp = typeof uni !== 'undefined' && uni.getStorageSync;

// ── 按端选择预设 ──
// PC 端使用 WEB_PRESETS（含 header-gradient 等 PC 特有字段）
// 移动端使用 UNI_PRESETS（不含 PC 特有字段，可独立扩展）
const ACTIVE_PRESETS = isUniApp ? UNI_PRESETS : WEB_PRESETS;
const ACTIVE_LAYOUT = isUniApp ? UNI_LAYOUT : WEB_LAYOUT;
const ACTIVE_SCHEME_NAMES = Object.keys(ACTIVE_PRESETS);

function getDocumentElement() {
  return globalThis.document?.documentElement ?? null;
}

// ── 内部工具 ──────────────────────────────────────────────

/**
 * 读取用户选择的配色方案（不存在则返回默认方案）
 */
function readStoredScheme() {
  const stored = cache.get(THEME_KEY, DEFAULT_SCHEME);
  if (ACTIVE_SCHEME_NAMES.includes(stored)) {
    return stored;
  }
  return DEFAULT_SCHEME;
}

// ── CSS 变量注入（PC / H5 端） ───────────────────────────

/**
 * 将配色方案预设应用到 CSS 变量
 *
 * 颜色字段 → --color-${key}
 * 布局字段 → --layout-${key}
 *
 * @param {string} schemeName - 方案名称
 * @param {HTMLElement} [target] - 目标元素（默认 document.documentElement）
 */
export function applyCssVars(schemeName, target) {
  const preset = ACTIVE_PRESETS[schemeName] || ACTIVE_PRESETS[DEFAULT_SCHEME];
  const layout = ACTIVE_LAYOUT[schemeName] || ACTIVE_LAYOUT[DEFAULT_SCHEME];
  const el = target || getDocumentElement();

  if (!el || !el.style) return;

  // 注入颜色变量
  for (const [key, value] of Object.entries(preset)) {
    el.style.setProperty(`--color-${key}`, value);
  }

  // 注入布局变量
  if (layout) {
    for (const [key, value] of Object.entries(layout)) {
      el.style.setProperty(`--layout-${key}`, value);
    }
  }
}

/**
 * 将远程配置应用到 CSS 变量
 * @param {Object} config - 远程主题配置
 * @param {HTMLElement} [target] - 目标元素
 */
export function applyRemoteVars(config, target) {
  if (!config || typeof config !== 'object') return;
  const el = target || getDocumentElement();
  if (!el || !el.style) return;

  for (const [key, value] of Object.entries(config)) {
    if (value !== undefined && value !== null) {
      el.style.setProperty(`--color-${key}`, String(value));
    }
  }
}

// ── 应用配色方案 ──────────────────────────────────────────

/**
 * 应用配色方案（PC / H5 / uni-app 统一入口）
 *
 * PC 端：注入 CSS 变量到 :root + 同步 Tailwind dark class
 * uni-app 端：更新 globalData（小程序端通过 getThemeStyleString + :style 消费）
 *
 * @param {string} schemeName - 配色方案名称
 */
export function applyScheme(schemeName) {
  // 注入 CSS 变量（PC / H5 端）
  applyCssVars(schemeName);

  // 同步 Tailwind dark class（PC / H5 端）
  // 通过 SCHEME_META 判断当前方案是否为深色风格
  const html = getDocumentElement();
  if (html) {
    const meta = SCHEME_META[schemeName];
    if (meta?.deep) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }

  // uni-app 全局同步（小程序端通过 globalData 传递）
  if (isUniApp) {
    const app = getApp?.();
    if (app) {
      app.globalData = app.globalData || {};
      app.globalData.theme = schemeName;
    }
  }
}

// ── 初始化 ────────────────────────────────────────────────

/**
 * 初始化主题（应在 main.js / App.vue onLaunch 中调用）
 *
 * 读取用户上次选择的配色方案并应用。
 * 不监听系统主题变化——配色方案由用户主动选择，与系统亮/暗模式无关。
 */
export function initTheme() {
  const schemeName = readStoredScheme();
  applyScheme(schemeName);
}

// ── 远程主题 ──────────────────────────────────────────────

/**
 * 从后端加载主题配置（品牌自定义主题）
 * @param {Function} fetcher - 返回 Promise<RemoteThemeConfig> 的请求函数
 * @param {Object} [options]
 * @param {boolean} [options.applyCached=true] - 是否先应用本地缓存的远程配置
 * @param {boolean} [options.saveCache=true] - 是否缓存新获取的配置
 */
export async function loadRemoteTheme(fetcher, options = {}) {
  const { applyCached = true, saveCache = true } = options;

  // 先应用本地缓存的远程配置（减少闪烁）
  if (applyCached) {
    const cached = cache.get(REMOTE_THEME_KEY);
    if (cached) applyRemoteVars(cached);
  }

  if (!fetcher) return;

  try {
    const config = await fetcher();
    if (!config || typeof config !== 'object') return;

    if (saveCache) {
      cache.set(REMOTE_THEME_KEY, config);
    }

    applyRemoteVars(config);
  } catch (e) {
    console.warn('[theme] 加载远程主题失败:', e);
  }
}

// ── 小程序端样式字符串生成 ───────────────────────────────

/**
 * 生成 CSS 变量 style 字符串（uni-app 小程序端使用）
 *
 * 小程序没有 document.documentElement，无法运行时注入 CSS 变量。
 * 替代方案：将此字符串绑定到根 <view> 的 :style 上。
 *
 * @param {string} schemeName - 配色方案名称
 * @param {Object} [remoteConfig] - 合并的远程配置
 * @returns {string} CSS 变量声明字符串，如 "--color-primary: #1677ff; ..."
 */
export function getThemeStyleString(schemeName, remoteConfig) {
  const preset = ACTIVE_PRESETS[schemeName] || ACTIVE_PRESETS[DEFAULT_SCHEME];
  const layout = ACTIVE_LAYOUT[schemeName] || ACTIVE_LAYOUT[DEFAULT_SCHEME];
  const merged = remoteConfig ? { ...preset, ...remoteConfig } : preset;

  const colorVars = Object.entries(merged)
    .map(([key, value]) => `--color-${key}: ${value}`)
    .join(';');

  const layoutVars = layout
    ? Object.entries(layout)
        .map(([key, value]) => `--layout-${key}: ${value}`)
        .join(';')
    : '';

  return layoutVars ? `${colorVars};${layoutVars}` : colorVars;
}

// ── Composable ─────────────────────────────────────────────

/**
 * 主题切换 Composable（组件 setup 中使用）
 *
 * 自动按端选择预设：PC 端使用 WEB_PRESETS，移动端使用 UNI_PRESETS。
 * 支持 PC 端和 uni-app 小程序端。
 * 小程序端需要配合 getThemeStyleString / useThemeStyle 获取 style 字符串。
 *
 * @returns {{
 *   schemeName: import('vue').ComputedRef<string>,
 *   setScheme: (name: string) => void,
 *   toggleScheme: () => void,
 *   THEME_PRESETS: object,
 *   THEME_LAYOUT: object,
 *   SCHEME_NAMES: string[],
 *   SCHEME_META: object,
 * }}
 */
export function useTheme() {
  const stored = ref(readStoredScheme());

  // 当前方案名称（对应 ACTIVE_SCHEME_NAMES 中的 key）
  const schemeName = computed(() => stored.value);

  /**
   * 切换到指定配色方案
   * @param {string} name - 方案名称（必须是当前端 ACTIVE_SCHEME_NAMES 中的值）
   */
  function setScheme(name) {
    if (!ACTIVE_SCHEME_NAMES.includes(name)) return;
    stored.value = name;
    cache.set(THEME_KEY, name);
    applyScheme(name);
  }

  /**
   * 在当前端可用方案间循环切换
   */
  function toggleScheme() {
    const idx = ACTIVE_SCHEME_NAMES.indexOf(stored.value);
    const next = ACTIVE_SCHEME_NAMES[(idx + 1) % ACTIVE_SCHEME_NAMES.length];
    setScheme(next);
  }

  // 当外部通过 cache 直接修改主题时，自动同步
  watch(schemeName, val => {
    applyScheme(val);
  });

  return {
    schemeName,
    setScheme,
    toggleScheme,
    /** @deprecated 使用 WEB_PRESETS / UNI_PRESETS 替代 */
    THEME_PRESETS: ACTIVE_PRESETS,
    /** 当前端布局常量 */
    THEME_LAYOUT: ACTIVE_LAYOUT,
    SCHEME_NAMES: ACTIVE_SCHEME_NAMES,
    SCHEME_META,
  };
}
